import React, { createContext, useContext, useState, useEffect } from 'react';
import { calculateMatchScore } from '../utils/matching';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const [profiles, setProfiles] = useState([]);
  const [matches, setMatches] = useState([]);
  const [matchRequests, setMatchRequests] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [companies, setCompanies] = useState([
    // Default companies
    'Microsoft',
    'Sony Interactive Entertainment',
    'Nintendo',
    'Electronic Arts',
    'Activision Blizzard',
    'Ubisoft',
    'Take-Two Interactive',
    'Epic Games',
    'Valve Corporation',
    'Square Enix',
    'Capcom',
    'Konami',
    'Sega',
    'Bandai Namco',
    'CD Projekt',
    'Rockstar Games',
    'Bethesda Game Studios',
    'Blizzard Entertainment',
    'Riot Games',
    'Supercell',
    'King Digital Entertainment',
    'Zynga',
    'Gameloft',
    'Rovio Entertainment',
    'Unity Technologies',
    'Unreal Engine',
    'Steam',
    'PlayStation Studios',
    'Xbox Game Studios',
    'Apple',
    'Google',
    'Amazon Game Studios',
    'Tencent Games',
    'NetEase Games',
    'miHoYo',
    'Indie Developer',
    'Freelancer'
  ]);

  // Form content configuration
  const [formContent, setFormContent] = useState({
    roles: ['Developer', 'Publisher', 'Investor', 'Mentor'],
    businessTypes: ['Indie Studio', 'AAA Studio', 'Publishing House', 'Investment Firm', 'Consulting', 'Individual'],
    platforms: ['PC', 'Mobile', 'Console', 'VR/AR', 'Web', 'Arcade'],
    genres: ['Action', 'Adventure', 'RPG', 'Strategy', 'Simulation', 'Puzzle', 'Horror', 'Racing', 'Sports', 'Casual'],
    markets: ['North America', 'Europe', 'Asia', 'Global', 'Emerging Markets'],
    needsOffers: ['Investment', 'Publishing', 'Mentorship', 'Partnership', 'Development', 'Marketing', 'Distribution', 'Technical Support'],
    experienceLevels: ['Beginner', 'Intermediate', 'Advanced', 'Expert'],
    companySizes: ['Solo', '2-10', '11-50', '51-200', '200+'],
    projectStages: ['Concept', 'Pre-Production', 'Production', 'Beta', 'Released'],
    budgetRanges: ['Under $10K', '$10K-$50K', '$50K-$250K', '$250K-$1M', '$1M+']
  });

  useEffect(() => {
    const storedProfiles = localStorage.getItem('962_games_matchmaker_profiles');
    const storedMatches = localStorage.getItem('962_games_matchmaker_matches');
    const storedRequests = localStorage.getItem('962_games_matchmaker_requests');
    const storedNotifications = localStorage.getItem('962_games_matchmaker_notifications');
    const storedCompanies = localStorage.getItem('962_games_matchmaker_companies');
    const storedFormContent = localStorage.getItem('962_games_matchmaker_form_content');

    if (storedProfiles) {
      setProfiles(JSON.parse(storedProfiles));
    }
    if (storedMatches) {
      setMatches(JSON.parse(storedMatches));
    }
    if (storedRequests) {
      setMatchRequests(JSON.parse(storedRequests));
    }
    if (storedNotifications) {
      setNotifications(JSON.parse(storedNotifications));
    }
    if (storedCompanies) {
      setCompanies(JSON.parse(storedCompanies));
    }
    if (storedFormContent) {
      setFormContent(JSON.parse(storedFormContent));
    }
  }, []);

  const saveProfile = (profile) => {
    const newProfile = {
      ...profile,
      id: profile.id || 'profile_' + Date.now(),
      createdAt: new Date().toISOString(),
      approved: false,
    };

    const updatedProfiles = profiles.filter(p => p.id !== newProfile.id);
    updatedProfiles.push(newProfile);
    setProfiles(updatedProfiles);
    localStorage.setItem('962_games_matchmaker_profiles', JSON.stringify(updatedProfiles));

    // Create notification for profile creation
    createNotification(
      newProfile.userId,
      'Profile Created',
      'Your profile has been submitted for review. You\'ll be notified once it\'s approved.',
      'profile'
    );

    return newProfile;
  };

  const updateProfile = (profileId, updates) => {
    const updatedProfiles = profiles.map(p =>
      p.id === profileId ? { ...p, ...updates, updatedAt: new Date().toISOString() } : p
    );
    setProfiles(updatedProfiles);
    localStorage.setItem('962_games_matchmaker_profiles', JSON.stringify(updatedProfiles));

    // If profile was approved, create notification
    if (updates.approved === true) {
      const profile = profiles.find(p => p.id === profileId);
      if (profile) {
        createNotification(
          profile.userId,
          'Profile Approved! 🎉',
          'Your profile has been approved and is now visible to other members.',
          'profile'
        );
      }
    }
  };

  const deleteProfile = (profileId) => {
    const updatedProfiles = profiles.filter(p => p.id !== profileId);
    setProfiles(updatedProfiles);
    localStorage.setItem('962_games_matchmaker_profiles', JSON.stringify(updatedProfiles));
  };

  const addCompany = (companyName) => {
    if (!companies.includes(companyName)) {
      const updatedCompanies = [...companies, companyName].sort();
      setCompanies(updatedCompanies);
      localStorage.setItem('962_games_matchmaker_companies', JSON.stringify(updatedCompanies));
    }
  };

  const removeCompany = (companyName) => {
    const updatedCompanies = companies.filter(c => c !== companyName);
    setCompanies(updatedCompanies);
    localStorage.setItem('962_games_matchmaker_companies', JSON.stringify(updatedCompanies));
  };

  const updateFormContent = (newContent) => {
    const updatedContent = { ...formContent, ...newContent };
    setFormContent(updatedContent);
    localStorage.setItem('962_games_matchmaker_form_content', JSON.stringify(updatedContent));
  };

  const createMatch = (profile1Id, profile2Id, score, isManual = false) => {
    const newMatch = {
      id: 'match_' + Date.now(),
      profile1Id,
      profile2Id,
      score,
      isManual,
      createdAt: new Date().toISOString(),
    };

    const updatedMatches = [...matches, newMatch];
    setMatches(updatedMatches);
    localStorage.setItem('962_games_matchmaker_matches', JSON.stringify(updatedMatches));

    // Create notifications for both profiles
    const profile1 = profiles.find(p => p.id === profile1Id);
    const profile2 = profiles.find(p => p.id === profile2Id);

    if (profile1 && profile2) {
      createNotification(
        profile1.userId,
        'New Match Found! 💫',
        `You've been matched with ${profile2.name} (${Math.round(score * 100)}% compatibility)`,
        'match'
      );
      createNotification(
        profile2.userId,
        'New Match Found! 💫',
        `You've been matched with ${profile1.name} (${Math.round(score * 100)}% compatibility)`,
        'match'
      );
    }

    return newMatch;
  };

  const recomputeMatches = () => {
    const approvedProfiles = profiles.filter(p => p.approved);
    const newMatches = [];

    for (let i = 0; i < approvedProfiles.length; i++) {
      for (let j = i + 1; j < approvedProfiles.length; j++) {
        const profile1 = approvedProfiles[i];
        const profile2 = approvedProfiles[j];
        const score = calculateMatchScore(profile1, profile2);

        if (score > 0.3) {
          const existingMatch = matches.find(m =>
            (m.profile1Id === profile1.id && m.profile2Id === profile2.id) ||
            (m.profile1Id === profile2.id && m.profile2Id === profile1.id)
          );

          if (!existingMatch) {
            newMatches.push({
              id: `match_${profile1.id}_${profile2.id}`,
              profile1Id: profile1.id,
              profile2Id: profile2.id,
              score,
              isManual: false,
              createdAt: new Date().toISOString(),
            });
          }
        }
      }
    }

    const allMatches = [...matches, ...newMatches];
    setMatches(allMatches);
    localStorage.setItem('962_games_matchmaker_matches', JSON.stringify(allMatches));

    // Create notifications for new matches
    newMatches.forEach(match => {
      const profile1 = profiles.find(p => p.id === match.profile1Id);
      const profile2 = profiles.find(p => p.id === match.profile2Id);

      if (profile1 && profile2) {
        createNotification(
          profile1.userId,
          'New Match Found! 💫',
          `You've been matched with ${profile2.name} (${Math.round(match.score * 100)}% compatibility)`,
          'match'
        );
        createNotification(
          profile2.userId,
          'New Match Found! 💫',
          `You've been matched with ${profile1.name} (${Math.round(match.score * 100)}% compatibility)`,
          'match'
        );
      }
    });
  };

  const createMatchRequest = (fromProfileId, toProfileId, message) => {
    const newRequest = {
      id: 'request_' + Date.now(),
      fromProfileId,
      toProfileId,
      message,
      status: 'pending',
      createdAt: new Date().toISOString(),
    };

    const updatedRequests = [...matchRequests, newRequest];
    setMatchRequests(updatedRequests);
    localStorage.setItem('962_games_matchmaker_requests', JSON.stringify(updatedRequests));

    // Create notification for the recipient
    const fromProfile = profiles.find(p => p.userId === fromProfileId);
    const toProfile = profiles.find(p => p.id === toProfileId);

    if (fromProfile && toProfile) {
      createNotification(
        toProfile.userId,
        'Connection Request 🤝',
        `${fromProfile.name} wants to connect with you!`,
        'connection'
      );
    }

    return newRequest;
  };

  const createNotification = (userId, title, message, type = 'general') => {
    const newNotification = {
      id: 'notification_' + Date.now(),
      userId,
      title,
      message,
      type,
      read: false,
      createdAt: new Date().toISOString(),
    };

    const updatedNotifications = [...notifications, newNotification];
    setNotifications(updatedNotifications);
    localStorage.setItem('962_games_matchmaker_notifications', JSON.stringify(updatedNotifications));

    return newNotification;
  };

  const markNotificationAsRead = (notificationId) => {
    const updatedNotifications = notifications.map(n =>
      n.id === notificationId ? { ...n, read: true } : n
    );
    setNotifications(updatedNotifications);
    localStorage.setItem('962_games_matchmaker_notifications', JSON.stringify(updatedNotifications));
  };

  const clearNotification = (notificationId) => {
    const updatedNotifications = notifications.filter(n => n.id !== notificationId);
    setNotifications(updatedNotifications);
    localStorage.setItem('962_games_matchmaker_notifications', JSON.stringify(updatedNotifications));
  };

  const value = {
    profiles,
    matches,
    matchRequests,
    notifications,
    companies,
    formContent,
    saveProfile,
    updateProfile,
    deleteProfile,
    addCompany,
    removeCompany,
    updateFormContent,
    createMatch,
    recomputeMatches,
    createMatchRequest,
    createNotification,
    markNotificationAsRead,
    clearNotification,
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};